/**
 * smc/sweeps.ts
 * Liquidity sweep detection.
 */
import { Candle, LiquiditySweep } from '../../../../types';
import { SwingPoint, SMCThresholds } from './types';

/**
 * A liquidity sweep occurs when price briefly exceeds a swing high/low
 * (collecting stop orders) then reverses back inside.
 *
 * The key distinguisher from a genuine breakout:
 * - Wick-only break (body stays inside) OR
 * - Close back inside within 1-2 bars
 *
 * Sweep + reclaim within 2 bars = highest-conviction reversal signal.
 */
export function detectLiquiditySweeps(
  candles: Candle[],
  swingHighs: SwingPoint[],
  swingLows: SwingPoint[],
  thresholds: SMCThresholds
): LiquiditySweep[] {
  const sweeps: LiquiditySweep[] = [];
  const lookback = thresholds.sweepLookback;
  const scanFrom = Math.max(0, candles.length - lookback);

  for (let i = scanFrom + 1; i < candles.length; i++) {
    const c = candles[i];

    for (const sh of swingHighs.filter(s => s.idx < i && s.idx >= i - lookback)) {
      if (c.high > sh.price && c.close <= sh.price * 1.002) {
        const reclaimed = c.close < sh.price;
        sweeps.push({
          type:      'sweep_high',
          level:     sh.price,
          candleIdx: i,
          ts:        c.ts,
          reclaimed,
          description: reclaimed
            ? `High sweep + reclaim at ${sh.price.toFixed(2)}: stop orders above cleared, price rejected — bearish reversal signal`
            : `High sweep at ${sh.price.toFixed(2)}: probed above swing high, watch for rejection close`,
        });
      }
    }

    for (const sl of swingLows.filter(s => s.idx < i && s.idx >= i - lookback)) {
      if (c.low < sl.price && c.close >= sl.price * 0.998) {
        const reclaimed = c.close > sl.price;
        sweeps.push({
          type:      'sweep_low',
          level:     sl.price,
          candleIdx: i,
          ts:        c.ts,
          reclaimed,
          description: reclaimed
            ? `Low sweep + reclaim at ${sl.price.toFixed(2)}: stop orders below cleared, price rejected — bullish reversal signal`
            : `Low sweep at ${sl.price.toFixed(2)}: probed below swing low, watch for reclaim`,
        });
      }
    }
  }

  sweeps.sort((a, b) => b.candleIdx - a.candleIdx);
  const deduped: LiquiditySweep[] = [];
  for (const s of sweeps) {
    const tooClose = deduped.some(d => Math.abs(d.level - s.level) / s.level < 0.003);
    if (!tooClose) deduped.push(s);
  }
  return deduped.slice(0, 4);
}
